import { z } from 'zod';
import { channelKind, isoTimestamp, pageQuery, severity, uuid } from './primitives.js';
import { deliveryMode, focusState } from './devices.js';
/** APNs device token: 32+ bytes hex, no separators. */
export const apnsConfig = z.object({
    device_token: z
        .string()
        .regex(/^[0-9a-fA-F]{64,200}$/, 'Ungültiges APNs Device Token (Hex erwartet)'),
    /** Overrides the server default bundle id, e.g. for the macOS companion app. */
    bundle_id: z.string().min(3).max(200).nullish(),
});
export const webhookConfig = z.object({
    url: z.url().max(2000).refine((u) => u.startsWith('https://') || u.startsWith('http://'), {
        message: 'Nur http(s) URLs',
    }),
    /** Sent as `X-MeshAlert-Signature` HMAC secret. Write-only. */
    secret: z.string().min(8).max(200).nullish(),
});
export const channel = z.object({
    id: uuid,
    user_id: uuid,
    user_name: z.string(),
    kind: channelKind,
    label: z.string(),
    is_active: z.boolean(),
    /** Secrets are redacted: device tokens are truncated, webhook secrets removed. */
    config_preview: z.string(),
    /** Nur bei apns_*-Kanälen gesetzt: was der Focus-Filter zuletzt gemeldet hat. */
    focus: focusState.nullish(),
    last_success_at: isoTimestamp.nullable(),
    last_error: z.string().nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const createChannelBody = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('apns_ios'),
        user_id: uuid.optional(),
        label: z.string().min(1).max(120),
        config: apnsConfig,
    }),
    z.object({
        kind: z.literal('apns_macos'),
        user_id: uuid.optional(),
        label: z.string().min(1).max(120),
        config: apnsConfig,
    }),
    z.object({
        kind: z.literal('webhook'),
        user_id: uuid.optional(),
        label: z.string().min(1).max(120),
        config: webhookConfig,
    }),
]);
export const updateChannelBody = z
    .object({
    label: z.string().min(1).max(120),
    is_active: z.boolean(),
    config: z.union([apnsConfig, webhookConfig]),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Mindestens ein Feld erforderlich' });
export const listChannelsQuery = pageQuery.extend({
    user_id: uuid.optional(),
    kind: channelKind.optional(),
});
/**
 * Body von `POST /v1/channels/:id/test`, insgesamt optional.
 *
 * `mode` gibt den Zustellmodus vor, statt ihn wie bei einem echten Alarm aus
 * Schwelle, Focus und Ruhezeiten abzuleiten. Ohne Apple-Freigabe wird
 * `critical` trotzdem auf `normal` gesenkt, sonst weist APNs den Payload ab.
 */
export const channelTestBody = z.object({
    severity: severity.default('info'),
    mode: deliveryMode.nullish(),
});
export const channelTestResponse = z.object({
    delivered: z.boolean(),
    detail: z.string(),
    severity,
    /** Der Modus, mit dem tatsächlich gesendet wurde. */
    mode: deliveryMode,
    reason: z.string(),
});
//# sourceMappingURL=channels.js.map